import { createServerError } from "./apiError";
import { triggerAuthError } from "../auth/authErrorHandler";
import { resolveApiBaseUrl } from "./apiBase";

// REST API service for components
interface ComponentField {
    uid: string;
    name: string;
    type: string;
    value?: string;
    order: number;
    required?: boolean;
    description?: string;
    quickSetValue?: string | number | boolean;
}

interface Component {
    uid: string;
    name: string;
    slug: string;
    collection: string;
    mongoCollection?: string;
    fields: ComponentField[];
    showGroups: boolean;
    public?: string;
    dataSource: 'mongodb'; // Explicitly identify the data source
    created?: string;
    updated?: string;
}

const API_URL = resolveApiBaseUrl();

const COMPONENT_DEBUG_LOGGING_ENABLED = false;
const debugLog = (...args: any[]) => {
    if (COMPONENT_DEBUG_LOGGING_ENABLED) {
        console.log(...args);
    }
};

// Helper function to check for Firebase audience mismatch and clear invalid tokens
const checkAndHandleFirebaseAudienceError = (errorText: string): boolean => {
    const errorLower = errorText.toLowerCase();
    if (errorLower.includes('incorrect "aud"') || errorLower.includes('audience') || errorLower.includes('demo-project')) {
        console.error('❌ ComponentsAPI: Firebase project mismatch detected. Clearing invalid token.');
        console.error('   This usually means the token was generated with a different Firebase project.');
        console.error('   Please sign in again to get a new token.');
        localStorage.removeItem('idToken');
        localStorage.removeItem('user');
        return true;
    }
    return false;
};

// Get auth token (aligned with dataApi / authApi: authToken first, then legacy idToken)
const getAuthToken = (): string | null => {
    const storages = [localStorage, sessionStorage];
    for (const storage of storages) {
        const authToken = storage.getItem('authToken');
        if (authToken?.trim()) {
            return authToken.trim();
        }
    }
    for (const storage of storages) {
        const legacyToken = storage.getItem('idToken');
        if (legacyToken?.trim()) {
            return legacyToken.trim();
        }
    }
    return null;
};

// Headers for API requests
const getHeaders = (includeContentType: boolean = true) => {
    const headers: Record<string, string> = {};

    // Only add Content-Type for requests with body (POST, PUT, etc.)
    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    // Add Bearer token from localStorage
    // Check localStorage directly first to debug
    const token = getAuthToken();

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        const tokenPreview = token.length > 20 ? `${token.substring(0, 10)}...${token.substring(token.length - 10)}` : '***';
        debugLog(`🔑 ComponentsAPI: Using token: ${tokenPreview} (length: ${token.length})`);
    } else {
        console.warn('⚠️ ComponentsAPI: No token found in storage - request will be unauthenticated');
    }

    debugLog(`📋 ComponentsAPI: Request headers:`, {
        hasContentType: !!headers['Content-Type'],
        hasAuthorization: !!headers['Authorization'],
        headerKeys: Object.keys(headers)
    });

    return headers;
};

const COMPONENTS_CACHE_TTL_MS = 60 * 1000;
let componentsCache: { components: Component[]; expiresAt: number; userKey: string } | null = null;

export function invalidateComponentsListCache(): void {
    componentsCache = null;
}

export type FetchComponentsOptions = {
    /** Cache and API results are scoped per signed-in user; avoids showing another account's collection list. */
    userUid?: string | null;
};

// Fetch all components from MongoDB API (which returns both Firebase and MongoDB components)
export const fetchComponentsFromAPI = async (options?: FetchComponentsOptions): Promise<Component[]> => {
    const userKey =
        options?.userUid != null && String(options.userUid).trim() !== ''
            ? String(options.userUid).trim()
            : '';
    const now = Date.now();
    if (
        componentsCache &&
        componentsCache.expiresAt > now &&
        componentsCache.userKey === userKey
    ) {
        return componentsCache.components;
    }
    try {
        // Get token first to ensure it exists
        const token = getAuthToken();
        if (!token) {
            console.error('❌ ComponentsAPI: No token available for request!');
            console.error('❌ ComponentsAPI: localStorage idToken:', localStorage.getItem('idToken') ? 'EXISTS' : 'MISSING');
            triggerAuthError();
            throw new Error('Authentication token not found. Please login again.');
        }

        debugLog('🔍 Fetching components from unified API (Firebase + MongoDB)...');
        const url = API_URL ? `${API_URL}/collections/components` : '/collections/components';
        debugLog(`📡 API URL: ${url} ${!API_URL ? '(proxied to localhost:3000)' : ''}`);
        debugLog(`🔑 Using token: ${token ? '***' + token.slice(-4) : 'NOT SET'}`);

        const headers = getHeaders(false); // Don't include Content-Type for GET requests

        // Verify token is in headers
        if (!headers['Authorization']) {
            console.error('❌ ComponentsAPI: Authorization header missing after getHeaders()!');
            console.error('❌ ComponentsAPI: Token exists:', !!token);
            console.error('❌ ComponentsAPI: Headers object:', headers);

            // Try to add it directly as a last resort
            const lastChanceToken = localStorage.getItem('idToken');
            if (lastChanceToken) {
                headers['Authorization'] = `Bearer ${lastChanceToken.trim()}`;
                console.log('⚠️ ComponentsAPI: Added Authorization header as last resort');
            } else {
                throw new Error('Failed to include authentication token in request');
            }
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: headers
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        debugLog(`📊 API Response Status: ${response.status} ${response.statusText}`);

        // Log response headers
        debugLog('📋 Response Headers:', Object.fromEntries(response.headers.entries()));

        const responseText = await response.text();
        debugLog('📄 Raw Response Body:', responseText);

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 403) {
                throw new Error('Permission denied. Access is not configured. Please contact an administrator.');
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        let data;
        try {
            data = JSON.parse(responseText);
        } catch (parseError) {
            console.error('❌ Failed to parse JSON response:', parseError);
            throw new Error(`Invalid JSON response: ${responseText}`);
        }

        debugLog('🎯 Parsed API Response:', data);

        if (!data.success) {
            throw new Error(data.message || 'API returned success: false');
        }

        // Handle both response formats: data.components or data.data
        let components = Array.isArray(data.components) ? data.components :
            Array.isArray(data.data) ? data.data : [];
        debugLog(`✅ Successfully fetched ${components.length} total components from unified API`);

        // Debug: Log the first component to see what fields are available
        if (components.length > 0) {
            debugLog('🔍 First component from API (raw):', components[0]);
            debugLog('🔍 All keys in first component:', Object.keys(components[0]));
            debugLog('🔍 First component fields:', {
                dataSource: components[0].dataSource,
                mongoCollection: components[0].mongoCollection,
                collection: components[0].collection,
                mongo_collection: components[0].mongo_collection,
                collections: components[0].collections
            });

            // Check if the fields are actually in the raw response
            debugLog('🔍 Checking for collection fields in raw response:');
            debugLog('  - mongoCollection exists:', 'mongoCollection' in components[0]);
            debugLog('  - mongoCollection value:', components[0].mongoCollection);
        }

        // Transform components, detecting dataSource based on component structure
        const transformedComponents = components.map((comp: any) => {
            // Debug: Log raw component data for collection fields
            debugLog(`🔍 Raw component data for ${comp.name}:`, {
                name: comp.name,
                slug: comp.slug,
                dataSource: comp.dataSource,
                mongoCollection: comp.mongoCollection,
                mongo_collection: comp.mongo_collection,
                collection: comp.collection,
                allKeys: Object.keys(comp)
            });

            // Debug the dataSource determination
            debugLog(`🔍 Determining dataSource for ${comp.name}:`, {
                compDataSource: comp.dataSource,
                hasId: !!comp._id,
                hasPublic: comp.public !== undefined,
                publicValue: comp.public,
                hasPublicValue: comp.public
            });

            // Use the API's dataSource field if available, otherwise detect based on component structure
            const dataSource = comp.dataSource === 'mongodb' ? 'mongodb' : 'mongodb';

            debugLog(`🔍 Final dataSource for ${comp.name}:`, dataSource, comp.dataSource ? '(from API)' : '(detected from structure)');

            // Determine collection based on dataSource
            let collection = comp.collection;
            if (!collection) {
                collection = comp.mongoCollection || comp.mongo_collection || comp.slug;
            }

            // Extract collection fields - check both camelCase and snake_case, and handle empty strings
            const mongoCollection = comp.mongoCollection || comp.mongo_collection;

            // Sanitize slug: remove trailing exclamation marks and trim whitespace
            const sanitizedSlug = typeof comp.slug === 'string' ? comp.slug.replace(/[!]+$/, '').trim() : comp.slug;

            const transformedComponent = {
                uid: comp._id || comp.uid || comp.id,
                name: comp.name,
                slug: sanitizedSlug,
                collection: collection,
                fields: comp.fields || [],
                showGroups: comp.showGroups || false,
                dataSource: dataSource,
                // Include the collection field - preserve undefined if not provided (don't use empty string)
                mongoCollection: mongoCollection && mongoCollection.trim() !== '' ? mongoCollection : undefined
            };

            // Debug: Log the transformed component
            debugLog(`🔄 Transformed component ${transformedComponent.name}:`, {
                dataSource: transformedComponent.dataSource,
                mongoCollection: transformedComponent.mongoCollection,
                collection: transformedComponent.collection
            });

            return {
                ...transformedComponent,
                public: comp.public,
                created: comp.created,
                updated: comp.updated
            };
        });

        const mongoCount = transformedComponents.filter((c: any) => c.dataSource === 'mongodb').length;
        debugLog(`Component transformation summary: ${mongoCount} MongoDB components`);
        debugLog('Sample transformed components:', transformedComponents.slice(0, 3));

        componentsCache = {
            components: transformedComponents,
            expiresAt: Date.now() + COMPONENTS_CACHE_TTL_MS,
            userKey
        };
        return transformedComponents;

    } catch (error: any) {
        console.error('❌ Unified API Error:', error);
        const apiError = error instanceof Error ? error : new Error('Failed to fetch components from API');
        throw apiError;
    }
};

// Create a new component
const createComponentAPI = async (component: Omit<Component, 'uid'>): Promise<Component> => {
    try {
        debugLog('➕ Creating component via MongoDB API:', component);

        // Get token first to ensure it exists
        const token = getAuthToken();
        if (!token) {
            console.error('❌ ComponentsAPI: No token available for POST request!');
            console.error('❌ ComponentsAPI: localStorage idToken:', localStorage.getItem('idToken') ? 'EXISTS' : 'MISSING');
            triggerAuthError();
            throw new Error('Authentication token not found. Please login again.');
        }

        const url = API_URL ? `${API_URL}/collections/components` : '/collections/components';
        const headers = getHeaders();

        // Verify token is in headers
        if (!headers['Authorization']) {
            console.error('❌ ComponentsAPI: Authorization header missing after getHeaders()!');
            console.error('❌ ComponentsAPI: Token exists:', !!token);
            console.error('❌ ComponentsAPI: Headers object:', headers);

            // Try to add it directly as a last resort
            const lastChanceToken = localStorage.getItem('idToken');
            if (lastChanceToken) {
                headers['Authorization'] = `Bearer ${lastChanceToken.trim()}`;
                console.log('⚠️ ComponentsAPI: Added Authorization header as last resort');
            } else {
                throw new Error('Failed to include authentication token in request');
            }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(component)
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();
        debugLog('📄 Create Response:', responseText);

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 403) {
                throw new Error('Permission denied. Access is not configured. Please contact an administrator.');
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to create component');
        }

        const createdComponent = {
            uid: data.data._id || data.data.uid,
            ...component,
            dataSource: 'mongodb' as const,
            created: data.data.created || new Date().toISOString(),
            updated: data.data.updated || new Date().toISOString()
        };

        debugLog('✅ Component created successfully:', createdComponent);
        return createdComponent;

    } catch (error) {
        console.error('❌ Failed to create component via API:', error);
        throw error;
    }
};

// Update a component
const updateComponentAPI = async (uid: string, updates: Partial<Component>): Promise<Component> => {
    try {
        debugLog(`✏️ Updating component ${uid} via MongoDB API:`, updates);

        // Get token first to ensure it exists
        const token = getAuthToken();
        if (!token) {
            console.error('❌ ComponentsAPI: No token available for PUT request!');
            console.error('❌ ComponentsAPI: localStorage idToken:', localStorage.getItem('idToken') ? 'EXISTS' : 'MISSING');
            throw new Error('Authentication token not found. Please login again.');
        }

        const url = API_URL ? `${API_URL}/collections/components/${uid}` : `/collections/components/${uid}`;
        const headers = getHeaders();

        // Verify token is in headers
        if (!headers['Authorization']) {
            console.error('❌ ComponentsAPI: Authorization header missing after getHeaders()!');
            console.error('❌ ComponentsAPI: Token exists:', !!token);
            console.error('❌ ComponentsAPI: Headers object:', headers);

            // Try to add it directly as a last resort
            const lastChanceToken = localStorage.getItem('idToken');
            if (lastChanceToken) {
                headers['Authorization'] = `Bearer ${lastChanceToken.trim()}`;
                console.log('⚠️ ComponentsAPI: Added Authorization header as last resort');
            } else {
                throw new Error('Failed to include authentication token in request');
            }
        }

        const response = await fetch(url, {
            method: 'PUT',
            headers: headers,
            body: JSON.stringify(updates)
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();
        debugLog('📄 Update Response:', responseText);

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 403) {
                throw new Error('Permission denied. Access is not configured. Please contact an administrator.');
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to update component');
        }

        const updatedComponent = {
            uid: data.data._id || data.data.uid,
            ...data.data,
            dataSource: 'mongodb' as const,
            updated: data.data.updated || new Date().toISOString()
        };

        debugLog('✅ Component updated successfully:', updatedComponent);
        return updatedComponent;

    } catch (error) {
        console.error('❌ Failed to update component via API:', error);
        throw error;
    }
};

// Delete a component
const deleteComponentAPI = async (uid: string): Promise<void> => {
    try {
        debugLog(`🗑️ Deleting component ${uid} via MongoDB API`);

        // Get token first to ensure it exists
        const token = getAuthToken();
        if (!token) {
            console.error('❌ ComponentsAPI: No token available for DELETE request!');
            console.error('❌ ComponentsAPI: localStorage idToken:', localStorage.getItem('idToken') ? 'EXISTS' : 'MISSING');
            throw new Error('Authentication token not found. Please login again.');
        }

        const url = API_URL ? `${API_URL}/collections/components/${uid}` : `/collections/components/${uid}`;
        const headers = getHeaders(false); // DELETE requests don't need Content-Type

        // Verify token is in headers
        if (!headers['Authorization']) {
            console.error('❌ ComponentsAPI: Authorization header missing after getHeaders()!');
            console.error('❌ ComponentsAPI: Token exists:', !!token);
            console.error('❌ ComponentsAPI: Headers object:', headers);

            // Try to add it directly as a last resort
            const lastChanceToken = localStorage.getItem('idToken');
            if (lastChanceToken) {
                headers['Authorization'] = `Bearer ${lastChanceToken.trim()}`;
                console.log('⚠️ ComponentsAPI: Added Authorization header as last resort');
            } else {
                throw new Error('Failed to include authentication token in request');
            }
        }

        const response = await fetch(url, {
            method: 'DELETE',
            headers: headers
        });

        if (response.status === 401) {
            localStorage.removeItem('idToken');
            localStorage.removeItem('user');
            throw new Error('Authentication required. Please login again.');
        }

        const responseText = await response.text();
        debugLog('📄 Delete Response:', responseText);

        if (!response.ok) {
            if (response.status >= 500) {
                throw createServerError(response.status);
            }
            if (response.status === 403) {
                throw new Error('Permission denied. Access is not configured. Please contact an administrator.');
            }
            // Check for Firebase audience mismatch error
            if (checkAndHandleFirebaseAudienceError(responseText)) {
                throw new Error('Authentication token is from a different Firebase project. Please sign in again.');
            }
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }

        const data = JSON.parse(responseText);

        if (!data.success) {
            throw new Error(data.message || 'Failed to delete component');
        }

        debugLog('✅ Component deleted successfully');

    } catch (error) {
        console.error('❌ Failed to delete component via API:', error);
        throw error;
    }
};

// Export the API functions
export {
    createComponentAPI,
    updateComponentAPI,
    deleteComponentAPI
};

// Export the main fetch function as default
export default fetchComponentsFromAPI; 